import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Confirm, Loading } from "@/components/common";
import { api, errMsg, downloadCsv } from "@/lib/api";
import { rupiah } from "@/lib/format";

const BLANK = { name: "", code: "", price: "", sort: 0, active: true };

function PriceRow({ row, idx, canEdit, onSave, onDelete }) {
  const [r, setR] = useState(row);
  useEffect(() => {
    setR(row);
  }, [row]);
  const dirty = JSON.stringify(r) !== JSON.stringify(row);
  const set = (k) => (e) => setR((s) => ({ ...s, [k]: e.target.value }));
  return (
    <tr data-testid={`price-row-${idx}`}>
      <td className="rownum">{idx + 1}</td>
      <td className="cell-edit"><input className={`xl-input text-left ${dirty ? "dirty" : ""}`} value={r.name} onChange={set("name")} disabled={!canEdit} data-testid={`price-name-${idx}`} /></td>
      <td className="cell-edit"><input className="xl-input text-left uppercase" value={r.code || ""} onChange={set("code")} disabled={!canEdit} data-testid={`price-code-${idx}`} /></td>
      <td className="cell-edit"><input type="number" min={0} className={`xl-input ${dirty ? "dirty" : ""}`} value={r.price} onChange={set("price")} disabled={!canEdit} data-testid={`price-value-${idx}`} /></td>
      <td className="num text-slate-500">{rupiah(r.price)}</td>
      <td className="cell-edit"><input type="number" className="xl-input" value={r.sort} onChange={set("sort")} disabled={!canEdit} /></td>
      <td className="text-center"><Switch checked={r.active} onCheckedChange={(v) => setR((s) => ({ ...s, active: v }))} disabled={!canEdit} data-testid={`price-active-${idx}`} /></td>
      {canEdit && (
        <td className="whitespace-nowrap">
          <Button size="sm" variant="ghost" disabled={!dirty} onClick={() => onSave(r)} data-testid={`price-save-${idx}`}><Save className="h-4 w-4" /></Button>
          <Confirm title="Hapus jenis voucher?" description={r.name} onConfirm={() => onDelete(r)} testId={`price-delete-${idx}`}
            trigger={<Button size="sm" variant="ghost" className="text-rose-600" data-testid={`price-delete-${idx}`}><Trash2 className="h-4 w-4" /></Button>} />
        </td>
      )}
    </tr>
  );
}

export function PriceManager({ apiBase, canEdit, exportable }) {
  const [rows, setRows] = useState(null);
  const [nw, setNw] = useState(BLANK);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(apiBase);
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [apiBase]);
  useEffect(() => {
    load();
  }, [load]);
  const payload = (r) => ({ name: r.name, code: r.code || "", price: Number(r.price) || 0, sort: Number(r.sort) || 0, active: !!r.active });
  const save = async (r) => {
    try { await api.put(`${apiBase}/${r.id}`, payload(r)); toast.success("Harga disimpan & tercatat di audit log"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => {
    try { await api.delete(`${apiBase}/${r.id}`); toast.success("Jenis voucher dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const add = async () => {
    if (!nw.name) return toast.error("Nama jenis voucher wajib diisi");
    try { await api.post(apiBase, payload({ ...nw, sort: rows.length })); setNw(BLANK); toast.success("Jenis voucher ditambahkan"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows) return <Loading />;
  return (
    <div className="space-y-3" data-testid="price-manager">
      {exportable && (
        <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => downloadCsv("/export/prices", "daftar-harga")} data-testid="price-export-btn"><Download className="h-4 w-4 mr-2" />Export CSV</Button></div>
      )}
      <div className="xl-wrap">
        <table className="xl">
          <thead><tr><th className="rownum">#</th><th>Jenis Voucher</th><th>Kode</th><th className="text-right">Harga (Rp)</th><th>Tampilan</th><th>Urutan</th><th>Aktif</th>{canEdit && <th>Aksi</th>}</tr></thead>
          <tbody>
            {rows.map((r, i) => <PriceRow key={r.id} row={r} idx={i} canEdit={canEdit} onSave={save} onDelete={del} />)}
            {canEdit && (
              <tr className="bg-emerald-50/40">
                <td className="rownum"><Plus className="h-3 w-3 mx-auto" /></td>
                <td className="cell-edit"><input className="xl-input text-left" placeholder="mis. Voucher 2 Jam" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} data-testid="price-new-name" /></td>
                <td className="cell-edit"><input className="xl-input text-left uppercase" placeholder="Kode" value={nw.code} onChange={(e) => setNw({ ...nw, code: e.target.value })} data-testid="price-new-code" /></td>
                <td className="cell-edit"><input type="number" min={0} className="xl-input" placeholder="0" value={nw.price} onChange={(e) => setNw({ ...nw, price: e.target.value })} data-testid="price-new-value" /></td>
                <td className="num text-slate-500">{rupiah(nw.price)}</td>
                <td /><td />
                <td><Button size="sm" onClick={add} className="bg-emerald-700 hover:bg-emerald-800" data-testid="price-add-btn">Tambah</Button></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
